import { BcsClass } from '@bioavail/shared-types';

export interface BcsEntry {
  cls: BcsClass;
  name: string;
  solubility: 'High' | 'Low';
  permeability: 'High' | 'Low';
  biowaiver: string;
  description: string;
}

export const BCS_REFERENCE: BcsEntry[] = [
  {
    cls: BcsClass.I,
    name: 'Class I',
    solubility: 'High',
    permeability: 'High',
    biowaiver: 'Eligible',
    description: 'High solubility, high permeability — favorable biowaiver candidate.',
  },
  {
    cls: BcsClass.II,
    name: 'Class II',
    solubility: 'Low',
    permeability: 'High',
    biowaiver: 'Case-by-case',
    description: 'Low solubility, high permeability — dissolution-limited absorption.',
  },
  {
    cls: BcsClass.III,
    name: 'Class III',
    solubility: 'High',
    permeability: 'Low',
    biowaiver: 'Case-by-case',
    description: 'High solubility, low permeability — permeability-limited absorption.',
  },
  {
    cls: BcsClass.IV,
    name: 'Class IV',
    solubility: 'Low',
    permeability: 'Low',
    biowaiver: 'Not eligible',
    description: 'Low solubility, low permeability — poor oral absorption.',
  },
];

export interface RegulatoryEntry {
  authority: string;
  acceptanceWindow: string;
  endpoints: string;
  notes: string;
}

export const REGULATORY_REFERENCE: RegulatoryEntry[] = [
  {
    authority: 'FDA',
    acceptanceWindow: '80.00–125.00% (90% CI)',
    endpoints: 'AUC(0-t), AUC(0-∞), Cmax',
    notes: 'TOST on log-transformed data; RSABE for HVDs (CV > 30%) pending jurisdiction.',
  },
  {
    authority: 'EMA',
    acceptanceWindow: '80.00–125.00% (90% CI)',
    endpoints: 'AUC(0-t), AUC(0-∞), Cmax',
    notes: 'Same TOST framework; HVD provisions via scaled-average bioequivalence.',
  },
];

export interface InlineDocEntry {
  key: string;
  title: string;
  body: string;
}

export interface PartialAucConvention {
  authority: 'FDA' | 'EMA';
  formulation: string;
  window: { start: number; end: number };
  notes: string;
}

/**
 * Partial-AUC regulatory conventions (F-075). Only well-documented guidance
 * windows are listed — do not invent conventions.
 */
export const PARTIAL_AUC_CONVENTIONS: PartialAucConvention[] = [
  {
    authority: 'FDA',
    formulation: 'Modified-release (MR)',
    window: { start: 0, end: 72 },
    notes: 'FDA guidance for MR products: partial AUC covers the relevant absorption window (pK assessment).',
  },
];

export const INLINE_DOCS: InlineDocEntry[] = [
  { key: 'f', title: 'Bioavailability (F)', body: 'The fraction of the administered dose that reaches systemic circulation unchanged. Computed as a dose-normalized AUC ratio of test vs reference.' },
  { key: 'frel', title: 'Relative bioavailability (F_rel)', body: 'The ratio of bioavailability of two formulations (test vs reference), not bounded by 100%.' },
  { key: 'auc', title: 'AUC', body: 'Area under the concentration–time curve — a measure of total drug exposure.' },
  { key: 'lambdaZ', title: 'λ_z (lambda-z)', body: 'Terminal elimination rate constant, estimated by log-linear regression on the terminal phase.' },
  { key: 'cmax', title: 'Cmax', body: 'Observed peak concentration.' },
  { key: 'tmax', title: 'Tmax', body: 'Time of observed peak concentration (earliest on tie).' },
  { key: 'halfLife', title: 'Half-life (t½)', body: 'Elimination half-life = ln(2)/λ_z.' },
  { key: 'clOverF', title: 'CL/F', body: 'Apparent total clearance = Dose / AUC(0-∞).' },
  { key: 'vzOverF', title: 'Vz/F', body: 'Apparent volume of distribution during terminal phase = CL/F / λ_z.' },
  { key: 'doseNorm', title: 'Dose normalization', body: 'When test and reference doses differ, F is corrected by the dose ratio so the comparison is exposure-per-mg.' },
  { key: 'blq', title: 'BLQ', body: 'Below the limit of quantification. M1 substitutes 0; M2 substitutes LLOQ/2.' },
];
